import type { LedgerItem, SaleItem } from '../api/admin.api'
import { addAmount, ledgerAmountDecimal, sumLedgerByCurrency } from './admin-money'
import type { MoneySummary } from './admin-money'

type ParticipantRole = 'producer' | 'affiliate' | 'coProducer' | 'seed' | 'referral' | 'other'

export type ParticipantSummary = {
  key: string
  role: ParticipantRole
  label: string
  recipientId: string
  currency: string
  total: string
  payable: string
  reserve: string
  debtSettled: string
  debtRemainder: string
  buckets: string[]
  statuses: string[]
}

type LedgerOwnerRole = ParticipantRole | 'vendepay' | 'gateway' | 'technical'

export type LedgerOwnerGroup = {
  key: string
  role: LedgerOwnerRole
  label: string
  subtitle: string
  recipientId?: string
  entries: LedgerItem[]
  totals: MoneySummary[]
}

const roleLabels: Record<ParticipantRole, string> = {
  producer: 'Produtor',
  affiliate: 'Afiliado',
  coProducer: 'Coprodutor',
  seed: 'Semente',
  referral: 'Indicador',
  other: 'Participante',
}

function normalizeRole(entry: LedgerItem, sale: SaleItem): ParticipantRole {
  const role = entry.role?.toLowerCase()
  if (role === 'producer') return 'producer'
  if (role === 'affiliate') return 'affiliate'
  if (role === 'co-producer' || role === 'coproducer') return 'coProducer'
  if (role === 'seed') return 'seed'
  if (role === 'referral') return 'referral'
  if (entry.recipientId === sale.producerId) return 'producer'
  if (entry.recipientId === sale.affiliateId) return 'affiliate'
  if (entry.recipientId === sale.coProducerId) return 'coProducer'
  if (entry.recipientId === sale.seedId) return 'seed'
  return 'other'
}

export function isGatewayLedgerEntry(entry: LedgerItem) {
  const haystack = `${entry.source} ${entry.role ?? ''} ${entry.type}`.toLowerCase()
  return haystack.includes('gateway') || haystack.includes('stripe')
}

export function isCurrencyFeeLedgerEntry(entry: LedgerItem) {
  return (
    entry.type === 'VENDEPAY' &&
    entry.role !== 'buyer-fx' &&
    (entry.source === 'CURRENCY_CONVERSION' || entry.role === 'fx-spread')
  )
}

export function isVendepaySaleFeeLedgerEntry(entry: LedgerItem) {
  return entry.type === 'VENDEPAY' && entry.source === 'SALE' && entry.role === 'fee'
}

function participantSummaries(entries: LedgerItem[], sale: SaleItem): ParticipantSummary[] {
  const summaries = new Map<
    string,
    ParticipantSummary & { bucketSet: Set<string>; statusSet: Set<string> }
  >()
  for (const entry of entries) {
    if (entry.type === 'VENDEPAY' || !entry.recipientId) continue
    const role = normalizeRole(entry, sale)
    const key = `${role}:${entry.recipientId}:${entry.amount.currency}`
    const current = summaries.get(key) ?? {
      key,
      role,
      label: roleLabels[role],
      recipientId: entry.recipientId,
      currency: entry.amount.currency,
      total: '0.000',
      payable: '0.000',
      reserve: '0.000',
      debtSettled: '0.000',
      debtRemainder: '0.000',
      buckets: [],
      statuses: [],
      bucketSet: new Set<string>(),
      statusSet: new Set<string>(),
    }
    const amount = ledgerAmountDecimal(entry)
    current.total = addAmount(current.total, amount)
    if (entry.type === 'RESERVE') {
      current.reserve = addAmount(current.reserve, amount)
    } else {
      current.payable = addAmount(current.payable, amount)
    }
    if (entry.status.includes('DEBT_SETTLED')) {
      current.debtSettled = addAmount(current.debtSettled, amount)
    }
    if (entry.source === 'DEBT_REMAINDER' && entry.status === 'PENDING') {
      current.debtRemainder = addAmount(current.debtRemainder, amount)
    }
    current.bucketSet.add(entry.amount.currency)
    current.statusSet.add(entry.status)
    summaries.set(key, current)
  }

  const order: Record<ParticipantRole, number> = {
    producer: 0,
    affiliate: 1,
    coProducer: 2,
    seed: 3,
    referral: 3,
    other: 4,
  }
  return [...summaries.values()]
    .map((item) => ({
      ...item,
      buckets: [...item.bucketSet],
      statuses: [...item.statusSet],
    }))
    .sort(
      (left, right) =>
        order[left.role] - order[right.role] || left.recipientId.localeCompare(right.recipientId),
    )
}

export function buildParticipantDisplaySummaries(
  entries: LedgerItem[],
  sale: SaleItem,
): ParticipantSummary[] {
  return participantSummaries(entries, sale)
}

export function ledgerEntryDisplayLabel(entry: LedgerItem) {
  if (entry.role === 'referral') {
    if (entry.type === 'VENDEPAY') return 'Comissão de indicação paga pela VendePay'
    if (entry.type === 'RESERVE') return 'Reserva de indicação'
    return 'Comissão de indicação'
  }
  if (entry.role === 'buyer-fx') return 'Conversão paga pelo comprador'
  if (isGatewayLedgerEntry(entry)) return 'Taxa do gateway'
  if (isCurrencyFeeLedgerEntry(entry)) return 'Taxa de conversão'
  if (isVendepaySaleFeeLedgerEntry(entry)) return 'Taxa Vendepay'
  if (entry.type === 'RESERVE') return 'Reserva'
  if (entry.type === 'USER') return 'Saldo do usuário'
  if (entry.type === 'VENDEPAY') return 'Vendepay'
  return entry.type
}

export function ledgerEntryDisplayHint(entry: LedgerItem) {
  const parts = [
    `origem ${entry.source}`,
    entry.phase ? `fase ${entry.phase}` : null,
    entry.kind ? `item ${entry.kind}` : null,
    `moeda ${entry.amount.currency}`,
  ].filter(Boolean)
  return parts.join(' · ')
}

function ledgerGroupMeta(entry: LedgerItem, sale: SaleItem) {
  if (isGatewayLedgerEntry(entry)) {
    return {
      key: 'gateway',
      role: 'gateway' as const,
      label: sale.gateway ? `Gateway ${sale.gateway}` : 'Gateway',
      subtitle: sale.gatewayPaymentId ?? 'Taxas e repasses do provedor',
    }
  }
  if (entry.type === 'VENDEPAY') {
    return {
      key: 'vendepay',
      role: 'vendepay' as const,
      label: 'VendePay antes dos custos',
      subtitle: 'Serviço, conversão e ajustes internos. Não representa resultado líquido.',
    }
  }
  if (entry.recipientId) {
    const role = normalizeRole(entry, sale)
    return {
      key: `${role}:${entry.recipientId}`,
      role,
      label: roleLabels[role],
      subtitle: 'Destinatário da venda',
      recipientId: entry.recipientId,
    }
  }
  return {
    key: `technical:${entry.type}:${entry.source}`,
    role: 'technical' as const,
    label: entry.role ?? entry.type,
    subtitle: 'Lançamento técnico sem destinatário',
  }
}

function ledgerOwnerGroups(entries: LedgerItem[], sale: SaleItem): LedgerOwnerGroup[] {
  const groups = new Map<string, Omit<LedgerOwnerGroup, 'totals'>>()
  for (const entry of entries) {
    const meta = ledgerGroupMeta(entry, sale)
    const current = groups.get(meta.key) ?? {
      ...meta,
      entries: [],
    }
    current.entries.push(entry)
    groups.set(meta.key, current)
  }

  const order: Record<LedgerOwnerRole, number> = {
    producer: 0,
    affiliate: 1,
    coProducer: 2,
    seed: 3,
    referral: 3,
    other: 4,
    vendepay: 5,
    gateway: 6,
    technical: 7,
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      totals: sumLedgerByCurrency(group.entries),
    }))
    .sort(
      (left, right) =>
        order[left.role] - order[right.role] || left.label.localeCompare(right.label),
    )
}

export function buildLedgerOwnerDisplayGroups(
  entries: LedgerItem[],
  sale: SaleItem,
): LedgerOwnerGroup[] {
  return ledgerOwnerGroups(entries, sale)
}

export function sumParticipantDisplayTotals(summaries: ParticipantSummary[]): MoneySummary[] {
  const totals = new Map<string, string>()
  for (const summary of summaries) {
    totals.set(summary.currency, addAmount(totals.get(summary.currency) ?? '0.000', summary.total))
  }
  return [...totals.entries()].map(([currency, amount]) => ({ currency, amount }))
}
